/**
 * documentsReport — Ordens de Serviço, Contratos e Recibos no período.
 *
 * O app construiu três entidades que o concorrente NÃO tem e não colhia nenhum
 * dado delas: até aqui havia ZERO relatório sobre `service_orders`, `contracts` e
 * `receipts`. Este módulo é a agregação pura (sem React, sem I/O) das três.
 *
 * ─── AS DECISÕES DE NEGÓCIO (rotuladas, não implícitas) ─────────────────────
 *
 * **1. A OS nascida de orçamento CONTA aqui — e é contada à parte na aba Lucro.**
 * No CMV da DRE (`profitReport.computeCogs`) uma OS com `quote_id` é PULADA: o
 * custo dela já está no orçamento de origem, e somar os dois dobraria o CMV.
 * Aqui não existe essa soma — o recorte é só de ordens de serviço —, então
 * excluí-las faria a contagem divergir da tela /ordens-servico e o usuário
 * concluiria que o relatório está quebrado. Elas entram, e quantas vieram de um
 * orçamento é DEVOLVIDO ({@link ServiceOrdersReport.fromQuoteCount}) e mostrado,
 * junto com a explicação de por que o custo aqui pode ser maior que o CMV da aba
 * Lucro. Divergência rotulada, nunca silenciosa.
 *
 * **2. `receipts` NÃO tem coluna `status`** (armadilha #57). O estado é DERIVADO:
 * `cancelled_at` preenchido = cancelado, senão emitido. Confirmado no
 * `information_schema` antes de escrever o acesso — `receipt.status` compilaria
 * (as linhas chegam como `any`) e renderizaria vazio para sempre.
 *
 * **3. Ausência de custo nunca é zero** (armadilha #61), igual ao resto: a margem
 * da OS sai só sobre a receita das linhas COM custo, e quantas ficaram de fora é
 * contado e mostrado. A conta de custo vem de `profitReport.ts`, não é
 * reimplementada.
 *
 * ─── BASE DE DATA ───────────────────────────────────────────────────────────
 *
 * Tudo aqui é a data do PRÓPRIO DOCUMENTO, nunca caixa:
 *  - OS e contrato: `created_at` — a MESMA base da aba Orçamentos, de propósito.
 *    Assim as abas de /relatorios falam todas do mesmo recorte de documentos e
 *    dá para conferir uma contra a outra. (A aba Lucro usa outra base — a
 *    competência `completed_at ?? created_at` — porque lá o assunto é quando o
 *    material foi consumido, e ela DIZ isso na tela.)
 *  - recibo: `issue_date` (é a data que o recibo declara; `created_at` seria a
 *    data em que alguém digitou, que pode ser semanas depois).
 * A conversão para 'YYYY-MM-DD' é feita no calendário LOCAL pelo chamador —
 * `.slice(0, 10)` daria a data em UTC e um documento das 21h30 do dia 31 cairia
 * no mês seguinte.
 */
import { roundMoney } from '@/lib/pricing';
import { cogsItemLineCost, isPassthroughLine, type CogsItem } from '@/lib/profitReport';
import { quoteItemLineTotal } from '@/lib/quoteTotals';
import type { IsoDateRange } from '@/lib/paymentsReport';

// ===== rótulos =====

export const SERVICE_ORDER_STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  in_progress: 'Em andamento',
  completed: 'Concluída',
  cancelled: 'Cancelada',
};

export const CONTRACT_STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  sent: 'Enviado',
  partially_signed: 'Parcial',
  signed: 'Assinado',
  cancelled: 'Cancelado',
};

/** Estado DERIVADO do recibo — a tabela não tem coluna `status` (#57). */
export type ReceiptState = 'issued' | 'cancelled';

export const RECEIPT_STATE_LABELS: Record<ReceiptState, string> = {
  issued: 'Emitido',
  cancelled: 'Cancelado',
};

export const DOCUMENTS_REPORT_SORT_FIELDS = [
  { value: 'date', label: 'Data' },
  { value: 'total', label: 'Valor' },
  { value: 'client', label: 'Cliente' },
  { value: 'status', label: 'Status' },
] as const;

export type DocumentsReportSortField = (typeof DOCUMENTS_REPORT_SORT_FIELDS)[number]['value'];
export type DocumentsReportSortDirection = 'asc' | 'desc';

export interface DocumentsReportSort {
  field: DocumentsReportSortField;
  direction: DocumentsReportSortDirection;
}

export const DEFAULT_DOCUMENTS_REPORT_SORT: DocumentsReportSort = {
  field: 'date',
  direction: 'desc',
};

export function documentSortServerColumn(
  field: DocumentsReportSortField,
  type: 'service_order' | 'contract' | 'receipt',
): string {
  if (field === 'date') return type === 'receipt' ? 'issue_date' : 'created_at';
  if (field === 'total') return type === 'receipt' ? 'amount' : 'total';
  if (field === 'client') return type === 'receipt' ? 'payer_name' : 'client_name';
  return type === 'receipt' ? 'cancelled_at' : 'status';
}

type SortableDocumentRow = {
  date: string;
  total?: number;
  amount?: number;
  clientName?: string;
  payerName?: string;
  status?: string;
  state?: string;
};

function sortDocumentRows<T extends SortableDocumentRow>(
  rows: T[],
  sort: DocumentsReportSort,
): void {
  const direction = sort.direction === 'asc' ? 1 : -1;
  const value = (row: T): string | number => {
    if (sort.field === 'date') return row.date;
    if (sort.field === 'total') return row.total ?? row.amount ?? 0;
    if (sort.field === 'client') return row.clientName ?? row.payerName ?? '';
    return row.status ?? row.state ?? '';
  };
  const stable = rows.map((row, index) => ({ row, index }));
  stable.sort((left, right) => {
    const a = value(left.row);
    const b = value(right.row);
    const aMissing = a == null || a === '';
    const bMissing = b == null || b === '';
    if (aMissing !== bMissing) return aMissing ? 1 : -1;
    const compared = typeof a === 'number' && typeof b === 'number'
      ? a - b
      : String(a).localeCompare(String(b), 'pt-BR', { sensitivity: 'base' });
    return compared * direction || left.index - right.index;
  });
  rows.splice(0, rows.length, ...stable.map(({ row }) => row));
}

/** Ordem canônica (estável entre sessões), usada nos gráficos e nas tabelas. */
const SERVICE_ORDER_STATUS_ORDER = ['pending', 'in_progress', 'completed', 'cancelled'];
const CONTRACT_STATUS_ORDER = ['draft', 'sent', 'partially_signed', 'signed', 'cancelled'];

/** Cor fixa por status (gráfico e legenda não mudam de cor entre sessões). */
export const SERVICE_ORDER_STATUS_COLORS: Record<string, string> = {
  pending: '#f59e0b',
  in_progress: '#0ea5e9',
  completed: '#10b981',
  cancelled: '#ef4444',
};

export const CONTRACT_STATUS_COLORS: Record<string, string> = {
  draft: '#94a3b8',
  sent: '#f59e0b',
  partially_signed: '#6366f1',
  signed: '#10b981',
  cancelled: '#ef4444',
};

/**
 * Rótulo CURTO para o eixo X (armadilha #51: o recharts esconde rótulos que
 * colidiriam e a barra fica anônima). "Em andamento" mede muito mais que a
 * largura por categoria numa tela de 360px.
 */
export const SERVICE_ORDER_SHORT_LABELS: Record<string, string> = {
  pending: 'Pend.',
  in_progress: 'Andam.',
  completed: 'Concl.',
  cancelled: 'Canc.',
};

export const CONTRACT_SHORT_LABELS: Record<string, string> = {
  draft: 'Rascun.',
  sent: 'Enviado',
  partially_signed: 'Parcial',
  signed: 'Assin.',
  cancelled: 'Canc.',
};

// ===== formas de entrada =====

export interface ServiceOrderLine extends CogsItem {
  name?: string | null;
  product_id?: string | null;
  unit_price?: number | string | null;
  discount?: number | string | null;
  discount_type?: string | null;
}

export interface ServiceOrderDocument {
  id: string;
  status: string | null;
  /** 'YYYY-MM-DD' de `created_at`, calendário LOCAL (ver a nota de base do topo). */
  date: string | null;
  total: number | string | null;
  /** Preenchido = nasceu de um orçamento. Conta aqui; ver a nota do topo. */
  quoteId?: string | null;
  clientId?: string | null;
  clientName?: string | null;
  title?: string | null;
  items: readonly ServiceOrderLine[];
}

export interface ContractDocument {
  id: string;
  status: string | null;
  /** 'YYYY-MM-DD' de `created_at`, calendário LOCAL. */
  date: string | null;
  total: number | string | null;
  clientId?: string | null;
  clientName?: string | null;
  title?: string | null;
  /** Preenchido quando as duas partes assinaram. */
  signedAt?: string | null;
}

export interface ReceiptDocument {
  id: string;
  /** 'YYYY-MM-DD' de `issue_date` (coluna DATE — já é a data local declarada). */
  date: string | null;
  amount: number | string | null;
  /** `cancelled_at` preenchido ⇒ cancelado. NÃO existe coluna `status` (#57). */
  cancelledAt?: string | null;
  clientId?: string | null;
  payerName?: string | null;
  paymentMethod?: string | null;
}

// ===== helpers =====

function money(raw: unknown): number {
  if (raw == null || raw === '') return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function inIsoRange(date: string | null | undefined, range: IsoDateRange): boolean {
  if (!date) return false;
  if (range.from && date < range.from) return false;
  if (range.to && date > range.to) return false;
  return true;
}

export interface StatusBucket {
  status: string;
  label: string;
  shortLabel: string;
  color: string;
  count: number;
  total: number;
}

function buildStatusBuckets(
  rows: { status: string | null; total: number }[],
  order: string[],
  labels: Record<string, string>,
  shortLabels: Record<string, string>,
  colors: Record<string, string>,
): StatusBucket[] {
  const map = new Map<string, { count: number; total: number }>();
  for (const row of rows) {
    const status = String(row.status ?? '') || 'unknown';
    const bucket = map.get(status) ?? { count: 0, total: 0 };
    bucket.count += 1;
    bucket.total = roundMoney(bucket.total + row.total);
    map.set(status, bucket);
  }

  return Array.from(map.entries())
    .map(([status, b]) => ({
      status,
      // Status desconhecido (linha antiga/futura) aparece com a chave crua em
      // vez de sumir do relatório.
      label: labels[status] ?? status,
      shortLabel: shortLabels[status] ?? labels[status] ?? status,
      color: colors[status] ?? '#94a3b8',
      count: b.count,
      total: b.total,
    }))
    .sort((a, b) => {
      const ia = order.indexOf(a.status);
      const ib = order.indexOf(b.status);
      return (
        (ia < 0 ? Number.MAX_SAFE_INTEGER : ia) - (ib < 0 ? Number.MAX_SAFE_INTEGER : ib)
        || a.label.localeCompare(b.label, 'pt-BR')
      );
    });
}

// ===== 1. Ordens de serviço =====

/** Status que representam serviço COMPROMETIDO — os mesmos do CMV do G5. */
export const OS_COMMITTED_STATUSES = ['in_progress', 'completed'] as const;

export interface ServiceOrderRow {
  id: string;
  date: string;
  status: string;
  statusLabel: string;
  title: string;
  clientName: string;
  total: number;
  /** Custo das linhas COM custo. `null` quando nenhuma linha tem. */
  cost: number | null;
  missingCostLines: number;
  fromQuote: boolean;
}

export interface ServiceOrdersReport {
  byStatus: StatusBucket[];
  /** Lista COMPLETA, da mais recente para a mais antiga. Quem corta é a tela. */
  rows: ServiceOrderRow[];
  count: number;
  total: number;
  /** Ticket médio das CONCLUÍDAS (as canceladas não são receita). */
  completedCount: number;
  completedTotal: number;
  /** Quantas nasceram de um orçamento — a divergência rotulada com a aba Lucro. */
  fromQuoteCount: number;
  /** Receita das linhas com custo, custo, margem — só das COMPROMETIDAS. */
  costedRevenue: number;
  cost: number;
  missingCostLines: number;
  margin: number | null;
  marginPercent: number | null;
  isEmpty: boolean;
}

/**
 * OS por status e por período, com margem.
 *
 * A margem só olha as ordens COMPROMETIDAS (`in_progress`/`completed`): uma OS
 * pendente ou cancelada não consumiu material, e incluí-la no custo faria a
 * margem despencar por causa de trabalho que talvez nunca aconteça. Contagem e
 * valor, ao contrário, olham TODAS — é um relatório de ordens de serviço, não
 * de serviços executados.
 */
export function computeServiceOrdersReport(
  documents: readonly ServiceOrderDocument[],
  range: IsoDateRange,
  sort: DocumentsReportSort = DEFAULT_DOCUMENTS_REPORT_SORT,
): ServiceOrdersReport {
  const scoped = (documents ?? []).filter((doc) => inIsoRange(doc?.date, range));

  const rows: ServiceOrderRow[] = [];
  let total = 0;
  let completedCount = 0;
  let completedTotal = 0;
  let fromQuoteCount = 0;
  let cost = 0;
  let costedRevenue = 0;
  let missingCostLines = 0;
  let hasCost = false;

  for (const doc of scoped) {
    const status = String(doc.status ?? '') || 'unknown';
    const docTotal = money(doc.total);
    total = roundMoney(total + docTotal);
    if (status === 'completed') {
      completedCount += 1;
      completedTotal = roundMoney(completedTotal + docTotal);
    }
    if (doc.quoteId) fromQuoteCount += 1;

    const committed = (OS_COMMITTED_STATUSES as readonly string[]).includes(status);
    let docCost = 0;
    let docHasCost = false;
    let docMissing = 0;

    for (const line of doc.items ?? []) {
      // `service_order_items` não tem `finance_entry_id` hoje, mas o guard fica:
      // se um dia a despesa cobrável chegar à OS (grupo 7), a linha de repasse
      // não pode virar mercadoria nem alarme falso de "sem custo".
      if (isPassthroughLine(line)) continue;
      const lineCost = cogsItemLineCost(line);
      if (lineCost === null) {
        docMissing += 1;
        continue;
      }
      docCost = roundMoney(docCost + lineCost);
      docHasCost = true;
      if (committed) costedRevenue = roundMoney(costedRevenue + quoteItemLineTotal(line));
    }

    if (committed) {
      cost = roundMoney(cost + docCost);
      missingCostLines += docMissing;
      if (docHasCost) hasCost = true;
    }

    rows.push({
      id: String(doc.id),
      date: String(doc.date ?? ''),
      status,
      statusLabel: SERVICE_ORDER_STATUS_LABELS[status] ?? status,
      title: String(doc.title ?? '').trim() || 'Ordem de serviço',
      clientName: String(doc.clientName ?? '').trim() || 'Sem cliente',
      total: docTotal,
      cost: docHasCost ? docCost : null,
      missingCostLines: docMissing,
      fromQuote: Boolean(doc.quoteId),
    });
  }

  sortDocumentRows(rows, sort);

  const margin = hasCost ? roundMoney(costedRevenue - cost) : null;

  return {
    byStatus: buildStatusBuckets(
      scoped.map((d) => ({ status: d.status, total: money(d.total) })),
      SERVICE_ORDER_STATUS_ORDER,
      SERVICE_ORDER_STATUS_LABELS,
      SERVICE_ORDER_SHORT_LABELS,
      SERVICE_ORDER_STATUS_COLORS,
    ),
    rows,
    count: scoped.length,
    total,
    completedCount,
    completedTotal,
    fromQuoteCount,
    costedRevenue,
    cost,
    missingCostLines,
    margin,
    marginPercent:
      margin !== null && costedRevenue > 0 ? (margin / costedRevenue) * 100 : null,
    isEmpty: scoped.length === 0,
  };
}

// ===== 2. Contratos =====

export interface ContractRow {
  id: string;
  date: string;
  status: string;
  statusLabel: string;
  title: string;
  clientName: string;
  total: number;
}

export interface ContractsReport {
  byStatus: StatusBucket[];
  rows: ContractRow[];
  count: number;
  total: number;
  /** Assinados = contrato fechado. É o número que importa para conversão. */
  signedCount: number;
  signedTotal: number;
  isEmpty: boolean;
}

export function computeContractsReport(
  documents: readonly ContractDocument[],
  range: IsoDateRange,
  sort: DocumentsReportSort = DEFAULT_DOCUMENTS_REPORT_SORT,
): ContractsReport {
  const scoped = (documents ?? []).filter((doc) => inIsoRange(doc?.date, range));

  let total = 0;
  let signedCount = 0;
  let signedTotal = 0;
  const rows: ContractRow[] = [];

  for (const doc of scoped) {
    const status = String(doc.status ?? '') || 'unknown';
    const docTotal = money(doc.total);
    total = roundMoney(total + docTotal);
    if (status === 'signed') {
      signedCount += 1;
      signedTotal = roundMoney(signedTotal + docTotal);
    }
    rows.push({
      id: String(doc.id),
      date: String(doc.date ?? ''),
      status,
      statusLabel: CONTRACT_STATUS_LABELS[status] ?? status,
      title: String(doc.title ?? '').trim() || 'Contrato',
      clientName: String(doc.clientName ?? '').trim() || 'Sem cliente',
      total: docTotal,
    });
  }

  sortDocumentRows(rows, sort);

  return {
    byStatus: buildStatusBuckets(
      scoped.map((d) => ({ status: d.status, total: money(d.total) })),
      CONTRACT_STATUS_ORDER,
      CONTRACT_STATUS_LABELS,
      CONTRACT_SHORT_LABELS,
      CONTRACT_STATUS_COLORS,
    ),
    rows,
    count: scoped.length,
    total,
    signedCount,
    signedTotal,
    isEmpty: scoped.length === 0,
  };
}

// ===== 3. Recibos =====

/**
 * Estado do recibo. NÃO existe `receipts.status` (#57) — `receipt.status`
 * compilaria (as linhas vêm como `any`) e renderizaria vazio para sempre.
 */
export function receiptState(doc: Pick<ReceiptDocument, 'cancelledAt'>): ReceiptState {
  return doc?.cancelledAt ? 'cancelled' : 'issued';
}

export interface ReceiptRow {
  id: string;
  date: string;
  state: ReceiptState;
  stateLabel: string;
  payerName: string;
  amount: number;
  paymentMethod: string | null;
}

export interface ReceiptsReport {
  rows: ReceiptRow[];
  count: number;
  /** Só os EMITIDOS somam: um recibo cancelado não comprova pagamento nenhum. */
  issuedCount: number;
  issuedTotal: number;
  cancelledCount: number;
  cancelledTotal: number;
  isEmpty: boolean;
}

export function computeReceiptsReport(
  documents: readonly ReceiptDocument[],
  range: IsoDateRange,
  sort: DocumentsReportSort = DEFAULT_DOCUMENTS_REPORT_SORT,
): ReceiptsReport {
  const scoped = (documents ?? []).filter((doc) => inIsoRange(doc?.date, range));

  let issuedCount = 0;
  let issuedTotal = 0;
  let cancelledCount = 0;
  let cancelledTotal = 0;
  const rows: ReceiptRow[] = [];

  for (const doc of scoped) {
    const state = receiptState(doc);
    const amount = money(doc.amount);
    if (state === 'cancelled') {
      cancelledCount += 1;
      cancelledTotal = roundMoney(cancelledTotal + amount);
    } else {
      issuedCount += 1;
      issuedTotal = roundMoney(issuedTotal + amount);
    }
    rows.push({
      id: String(doc.id),
      date: String(doc.date ?? ''),
      state,
      stateLabel: RECEIPT_STATE_LABELS[state],
      payerName: String(doc.payerName ?? '').trim() || 'Sem pagador',
      amount,
      paymentMethod: doc.paymentMethod ? String(doc.paymentMethod) : null,
    });
  }

  sortDocumentRows(rows, sort);

  return {
    rows,
    count: scoped.length,
    issuedCount,
    issuedTotal,
    cancelledCount,
    cancelledTotal,
    isEmpty: scoped.length === 0,
  };
}
